import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { navLinks } from "@/data/content";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `editorial-link text-sm font-medium tracking-wide transition-colors duration-300 ${
      isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
    }`;

  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50 border-b border-border/70 bg-background/85 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
        <Link to="/" data-testid="brand-logo-link" className="group flex items-center gap-3" aria-label="Bello Joy Consulting home">
          <span className="flex h-11 w-11 items-center justify-center rounded-full border border-secondary/50 bg-primary font-serif text-lg italic text-primary-foreground transition-transform duration-300 group-hover:rotate-6">
            BJ
          </span>
          <span className="leading-tight">
            <span className="block font-serif text-xl tracking-tight text-primary">Bello Joy</span>
            <span className="block text-[11px] font-medium uppercase tracking-[0.28em] text-secondary">Consulting</span>
          </span>
        </Link>

        <nav data-testid="desktop-navigation" className="hidden items-center gap-8 lg:flex" aria-label="Main navigation">
          {navLinks.map((link) => (
            <NavLink key={link.href} to={link.href} data-testid={`nav-${link.label.toLowerCase().replaceAll(" ", "-")}-link`} className={linkClass}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button asChild className="rounded-full bg-primary px-6 text-primary-foreground transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-primary/90">
            <Link to="/contact" data-testid="nav-schedule-consultation-button">Schedule a Consultation</Link>
          </Button>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-primary lg:hidden"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.28 }}
            data-testid="mobile-menu-panel"
            className="border-t border-border bg-background px-6 py-8 shadow-soft lg:hidden"
          >
            <nav className="flex flex-col gap-5" aria-label="Mobile navigation">
              {navLinks.map((link, index) => (
                <motion.div key={link.href} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}>
                  <NavLink to={link.href} data-testid={`mobile-nav-${link.label.toLowerCase().replaceAll(" ", "-")}-link`} className="font-serif text-3xl text-primary">
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <Button asChild className="mt-4 rounded-full bg-primary py-6 text-primary-foreground">
                <Link to="/contact" data-testid="mobile-schedule-consultation-button">Schedule a Consultation</Link>
              </Button>
            </nav>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
};
